(() => {
  'use strict';
  if (window.ogSecaLandingInitialized) return;
  window.ogSecaLandingInitialized = true;
  const instances = new Map();

  function mountReport(root, signal, updateSticky) {
    const gallery = root.querySelector('[data-seca-report]');
    if (!gallery) return () => {};
    const dialog = gallery.querySelector('[data-seca-report-dialog]');
    if (typeof dialog?.showModal !== 'function') return () => {};
    const links = [...gallery.querySelectorAll('[data-seca-report-open]')];
    const image = dialog.querySelector('[data-seca-report-full]');
    const canvas = dialog.querySelector('[data-seca-report-canvas]');
    const status = dialog.querySelector('[data-seca-report-status]');
    const previous = dialog.querySelector('[data-seca-report-prev]');
    const next = dialog.querySelector('[data-seca-report-next]');
    const zoom = dialog.querySelector('[data-seca-report-zoom]');
    let current = 0;
    let opener;
    let previousOverflow;

    function setZoom(enabled) {
      canvas.classList.toggle('is-zoomed', enabled);
      zoom.setAttribute('aria-pressed', String(enabled));
      zoom.textContent = enabled ? zoom.dataset.fitLabel : zoom.dataset.zoomLabel;
      canvas.scrollTop = 0;
      canvas.scrollLeft = 0;
    }

    function showPage(index) {
      current = Math.max(0, Math.min(links.length - 1, index));
      const link = links[current];
      image.src = link.href;
      image.alt = link.querySelector('img').alt;
      status.textContent = link.dataset.reportTitle;
      previous.disabled = current === 0;
      next.disabled = current === links.length - 1;
      setZoom(false);
    }

    function restorePage() {
      if (previousOverflow !== undefined) {
        document.body.style.overflow = previousOverflow;
        previousOverflow = undefined;
      }
      updateSticky();
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    }

    links.forEach((link, index) => {
      link.setAttribute('aria-haspopup', 'dialog');
      link.addEventListener('click', (event) => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        opener = link;
        showPage(index);
        previousOverflow = document.body.style.overflow;
        dialog.showModal();
        document.body.style.overflow = 'hidden';
        updateSticky();
      }, { signal });
    });
    dialog.querySelector('[data-seca-report-close]').addEventListener('click', () => dialog.close(), { signal });
    dialog.addEventListener('close', restorePage, { signal });
    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
    }, { signal });
    previous.addEventListener('click', () => { showPage(current - 1); if (previous.disabled) next.focus(); }, { signal });
    next.addEventListener('click', () => { showPage(current + 1); if (next.disabled) previous.focus(); }, { signal });
    zoom.addEventListener('click', () => setZoom(zoom.getAttribute('aria-pressed') !== 'true'), { signal });
    return () => { if (dialog.open) dialog.close(); restorePage(); };
  }

  function mount(root) {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const { signal } = controller;
    const sticky = root.querySelector('[data-seca-sticky]');
    const hero = root.querySelector('[data-seca-cta="hero"]');
    const final = root.querySelector('[data-seca-booking]');
    const media = window.matchMedia('(max-width: 599px)');
    let pastHero = false;
    let atFinal = false;
    let viewed = false;

    const analyticsAllowed = () => root.dataset.analytics === 'true'
      && root.dataset.editor !== 'true'
      && window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;

    function emit(event, position, destination) {
      if (!analyticsAllowed()) return;
      // Allowlisted context only: no URL, user identifier, form data or body values.
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event, seca_placement: position, seca_destination: destination });
    }

    function pageView() {
      if (viewed || !analyticsAllowed()) return;
      viewed = true;
      emit('og_seca_page_view', 'page', 'none');
    }

    function updateSticky() {
      if (!sticky) return;
      // Keep existing consent, navigation and cart overlays unobstructed.
      const overlayOpen = document.body.classList.contains('overflow-hidden')
        || root.querySelector('[data-seca-report-dialog]')?.open === true
        || [...document.querySelectorAll('[x-data*="xCookieBanner"]')].some((el) => {
          const box = el.getBoundingClientRect();
          const style = getComputedStyle(el);
          return box.width > 0 && box.height > 0 && style.visibility !== 'hidden' && style.display !== 'none';
        });
      sticky.hidden = !media.matches || !pastHero || atFinal || overlayOpen;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === hero) pastHero = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        if (entry.target === final) atFinal = entry.isIntersecting;
      });
      updateSticky();
    });
    observer.observe(hero);
    if (final) observer.observe(final);
    const overlayObserver = new MutationObserver(updateSticky);
    overlayObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    document.querySelectorAll('[x-data*="xCookieBanner"]').forEach((el) => {
      overlayObserver.observe(el, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
    });
    media.addEventListener('change', updateSticky, { signal });
    document.addEventListener('visitorConsentCollected', pageView, { signal });
    document.addEventListener('eurus:customer-privacy:loaded', pageView, { signal });
    root.addEventListener('click', (event) => {
      const link = event.target.closest('a[data-seca-cta]');
      if (!link || !root.contains(link)) return;
      pageView();
      const position = link.dataset.secaCta;
      const destination = link.dataset.secaDestination;
      emit(position === 'hero' ? 'og_seca_primary_click' : 'og_seca_cta_click', position, destination);
      // A click is not a started or successful booking. Those require provider evidence.
    }, { signal });
    pageView();
    const destroyReport = mountReport(root, signal, updateSticky);
    instances.set(root, () => { destroyReport(); controller.abort(); observer.disconnect(); overlayObserver.disconnect(); });
  }

  function init(scope) {
    scope.querySelectorAll('[data-seca-root]').forEach(mount);
  }
  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((destroy, root) => {
      if (event.target.contains(root)) { destroy(); instances.delete(root); }
    });
  });
})();
